import type { Dict } from '@/types/dict'
import { isRef } from 'vue'
import { isNumericalString } from '../is'
import { insert } from './insert'

export type ObjectDiff<T = unknown> = {
  path: string
  oldValue: T
  newValue: T
}

export type ObjectDiffOptions = {
  compareNonNullishOnly?: boolean
  keyFilter?: (key: string) => boolean
  maxDepth?: number
  debug?: boolean
}

export const getObjectDiffs = (
  oldObject: Dict,
  newObject: Dict,
  options: ObjectDiffOptions = { compareNonNullishOnly: true }
): ObjectDiff[] => {
  const getDiffs = (
    oldObject: Dict,
    newObject: Dict,
    path: string = '',
    depth: number = 0
  ): ObjectDiff[] => {
    const diffs: ObjectDiff[] = []

    const keys = new Set([...Object.keys(oldObject || {}), ...Object.keys(newObject || {})])

    for (const key of keys) {
      //      if (options.keyFilter && options.keyFilter(key) !== true) continue;

      const fullPath = path ? `${path}.${key}` : key
      const oldValue = oldObject ? oldObject[key] : undefined
      const newValue = newObject ? newObject[key] : undefined

      if (options.compareNonNullishOnly) {
        if (typeof newValue === 'undefined') continue // || newValue === null) continue;
      }

      if (oldValue === newValue) {
        continue
      }

      if (
        Array.isArray(oldValue) &&
        Array.isArray(newValue)
      ) {
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          diffs.push({ path: fullPath, oldValue, newValue })
        }
        continue
      }

      if (
        typeof oldValue === 'object' &&
        oldValue !== null &&
        typeof newValue === 'object' &&
        newValue !== null &&
        !Array.isArray(oldValue) &&
        !Array.isArray(newValue)
      ) {
        diffs.push(...getDiffs(oldValue, newValue, fullPath, depth + 1))
      } else {
        diffs.push({ path: fullPath, oldValue, newValue })
      }
    }

    return diffs
  }

  return getDiffs(oldObject, newObject)
}

export const applyObjectDiffs = <T extends Dict | Array<any>>(
  targetObject: T,
  diffs: ObjectDiff[],
  clone: (obj: T) => T = (x) => x //JSON.parse(JSON.stringify(x)),
): T => {
  const resultObject = clone(targetObject);
  for (const { path, newValue } of diffs) {

    const keys = path.split('.');
    const finalKey = keys[keys.length - 1]
    let current: any = resultObject as any;

    if (isNumericalString(finalKey)) {
      console.log(`INSERT`, { path, newValue });
      insert(resultObject, path, newValue);
      console.log({ resultObject });
      continue;
    }

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!(key in current) || (typeof current[key] !== 'object' && !Array.isArray(current[key]))) {
        (current as any)[key] = {};
      }

      current = current[key]
    }


    if (typeof newValue === 'undefined') continue

    if (isRef(current[finalKey])) {
      current[finalKey].value = newValue
      continue
    }
    current[finalKey] = newValue
  }

  return resultObject
}

export const diffAndPatch = <T extends Dict>(
  props: {
    oldObject?: T,
    newObject: T,
  },
  options: ObjectDiffOptions = { compareNonNullishOnly: true }
): T => {
  if (!props.oldObject) return props.newObject;
  const diff = getObjectDiffs(props.oldObject || {}, props.newObject || {}, options);
  applyObjectDiffs(props.oldObject || {}, diff, (x) => x);
  return props.oldObject;
}
